import { Link } from "react-router-dom";
import { student_routes } from "../../../router/all_routes";
import { useAcademicYear } from "../../../../core/common/context/AcademicYearContext";
import EDTTooltipOption from "./tooltipOption";
import { useEffect, useState } from "react";
import { getEDTByClass } from "../../../../api/edt";
import { toast } from "sonner";
import {
  getBreaksByYear,
  getSessionsByYear,
  getSettingByYear,
} from "../../../../api/settingEDT";
import {
  Action,
  buildPermissionContext,
  Resource,
} from "../../../../core/common/autorisations/permissionsMap";
import { toMinutes } from "../../../../helper/toMinutes";
import StudentEDTGrid from "./StudentEDTGrid";

const StudentEDT = () => {
  const routes = student_routes;

  const { selectedYear } = useAcademicYear();
  const [days, setDays] = useState<any[]>([]);
  const [sessions, setSessions] = useState<any[]>([]);
  const [allBreaks, setAllBreaks] = useState<any[]>([]);
  const [courses, setCourses] = useState<any>({});
  const [selectedTimetable, setSelectedTimetable] = useState<any>();
  const [selectedTimetableData, setSelectedTimetableData] = useState<any>();
  const [loadingEDT, setLoadingEDT] = useState<boolean>(false);

  //  fonction pour récupérer les cours d'un EDTVersion
  const fetchAllCourseOfEDT = async () => {
    try {
      setLoadingEDT(true);
      if (!selectedYear || !selectedTimetable) {
        return;
      }

      const response = await getEDTByClass(
        selectedYear?.Student?.Class?.id,
        selectedTimetable?.VersionEDTs[0]?.id,
        buildPermissionContext(Resource.EDT, Action.READ),
      );
      // console.log("Courses Response: ", response);
      setSelectedTimetableData(response.data[0]);
    } catch (error: any) {
      setLoadingEDT(false);
      // console.log("erreur de chargement des coursEDTs : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  useEffect(() => {
    fetchAllCourseOfEDT();
  }, [selectedTimetable, selectedYear]);

  // fonction pour la récupération des jours ouvrables
  const fetchWorkingDays = async () => {
    try {
      if (!selectedYear?.id) return;
      const response = await getSettingByYear(
        selectedYear.id,
        buildPermissionContext(Resource.EDT, Action.READ),
      );

      // Convertit la chaîne JSON reçue en tableau de jours
      const rawDays: string = response.data[0].working_days; // ex: "{\"Lundi\",\"Dimanche\"}"
      const cleanedDays = rawDays.replace(/[{}]/g, ""); // retire accolades
      const daysArray = cleanedDays
        .split(",")
        .map((d) => d.replace(/"/g, "").trim()); // ["Lundi", "Dimanche"]

      // Ordre des jours de la semaine
      const weekOrder = [
        "Lundi",
        "Mardi",
        "Mercredi",
        "Jeudi",
        "Vendredi",
        "Samedi",
        "Dimanche",
      ];
      const sortedDays = daysArray.sort(
        (a, b) => weekOrder.indexOf(a) - weekOrder.indexOf(b),
      );

      const formattedDays = sortedDays.map((day: any, idx: number) => ({
        id: `d${idx + 1}`,
        name: day,
      }));

      setDays(formattedDays);
    } catch (error: any) {
      // console.log("erreur de chargement des jours ouvrables : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  // fonction pour récupérer toutes les séances
  const fetchAllSessions = async () => {
    try {
      if (!selectedYear?.id) return;

      const response = await getSessionsByYear(selectedYear?.id);
      const formattedSessions = response.data.map(
        (session: any, index: number) => ({
          label: session.name,
          start_time: session.start_time?.slice(0, 5), // pour enlever les secondes
          end_time: session.end_time?.slice(0, 5),
          time: `${session.start_time?.slice(0, 5)} - ${session.end_time?.slice(
            0,
            5,
          )}`,
          index: index + 1,
          id: session.id,
          duration:
            toMinutes(session.end_time?.slice(0, 5)) -
            toMinutes(session.start_time?.slice(0, 5)),
        }),
      );
      setSessions(formattedSessions);
      // console.log("Seances récupéré: ",formattedSessions)
    } catch (error: any) {
      // console.log("erreur de chargement des séances : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  // Fonction pour récupérer les pauses d'une année
  const fetchEDTBreaksByYear = async () => {
    try {
      if (!selectedYear) return;
      const response = await getBreaksByYear(
        selectedYear?.id,
        buildPermissionContext(Resource.SETTING_EDT, Action.READ),
      );
      // console.log("response data break: ",response.data)
      const formattedBreaks = response.data.map(
        (breakItem: any, index: number) => ({
          id: breakItem.id,
          label: breakItem.name,
          start: breakItem.start_time?.slice(0, 5), // pour enlever les secondes
          end: breakItem.end_time?.slice(0, 5),
        }),
      );
      // console.log("response data formatted break: ",formattedBreaks)
      setAllBreaks(formattedBreaks);
    } catch (error: any) {
      // console.log("erreur lors de la récupération des pauses : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  // fonction pour formater les cours
  const formattedCourses = () => {
    if (
      !selectedTimetableData?.VersionEDTs ||
      selectedTimetableData.VersionEDTs.length === 0
    ) {
      setLoadingEDT(false);
      return {};
    }

    const version = selectedTimetableData.VersionEDTs[0];
    if (!version.CourseVersionEDTs) {
      setLoadingEDT(false);
      return setCourses({});
    }

    const coursesData: any = {};

    // Grouper les cours par jour, séance de début et séance de fin
    const groupedBySessionTime: { [key: string]: any[] } = {};
    // console.log("All Courses: ", version.CourseVersionEDTs);
    version.CourseVersionEDTs.forEach((courseVersion: any) => {
      const day = courseVersion.day_course;
      const sessionStartId = courseVersion.SessionStart?.id;
      const sessionEndId = courseVersion.SessionEnd?.id;

      // Créer une clé unique basée sur le jour et les séances
      const key = `${day}_${sessionStartId}_${sessionEndId}`;

      if (!groupedBySessionTime[key]) {
        groupedBySessionTime[key] = [];
      }

      groupedBySessionTime[key].push(courseVersion);
    });

    // Traiter chaque groupe et créer la structure finale
    Object.entries(groupedBySessionTime).forEach(([key, coursesInSlot]) => {
      // Filtrer pour éviter les doublons de cours (même matière, même prof, même salle)
      const uniqueCourses: { [courseKey: string]: any } = {};

      coursesInSlot.forEach((courseVersion: any) => {
        const courseId = courseVersion?.Course?.id;
        const subjectLabel =
          courseVersion?.Course?.Subject?.label || "Matière inconnue";
        const courseKey = `${courseId}_${subjectLabel}`;

        if (!uniqueCourses[courseKey]) {
          uniqueCourses[courseKey] = {
            name: subjectLabel,
            id: courseVersion?.course_version_id || courseId,
            room: courseVersion.Classroom?.label || "Salle inconnue",
            teacherId: courseVersion?.Course?.TeacherProfil?.id,
            teacher: `${courseVersion.Course?.TeacherProfil?.firstname || ""} ${
              courseVersion?.Course?.TeacherProfil?.lastname || ""
            }`.trim(),
            classes: [],
            courseVersionIds: [],
            sessionStart: courseVersion.SessionStart,
            sessionEnd: courseVersion.SessionEnd,
            state_course: courseVersion.state_course,
            type_course: courseVersion.type_course,
            date_course: courseVersion.date_course,
            classroom: courseVersion.Classroom,
          };
        }

        // Ajouter la classe si elle n'existe pas déjà
        const classLabel = courseVersion?.Class
          ? `${courseVersion?.Class.Level?.abbreviation || ""}-${
              courseVersion?.Class.abbreviation || ""
            }`.trim()
          : "Classe inconnue";

        if (
          !uniqueCourses[courseKey].classes.some(
            (c: any) => c.id === courseVersion?.Class?.id,
          )
        ) {
          uniqueCourses[courseKey].classes.push({
            id: courseVersion?.Class?.id,
            label: classLabel,
            fullLabel: courseVersion?.Class?.label || "Classe inconnue",
          });
        }
        // Ajouter l'ID de la version de cours si elle n'existe pas déjà
        if (
          !uniqueCourses[courseKey].courseVersionIds.some(
            (c: any) => c === courseVersion?.course_version_id,
          )
        ) {
          uniqueCourses[courseKey].courseVersionIds.push(
            courseVersion?.course_version_id || "",
          );
        }
      });

      // Remplir la structure des cours par jour et séance
      const firstCourse = coursesInSlot[0];
      const day = firstCourse.day_course;
      const sessionId = firstCourse.SessionStart?.id;

      if (!coursesData[day]) {
        coursesData[day] = {};
      }

      // Ajouter chaque cours unique sous la clé de la séance
      Object.entries(uniqueCourses).forEach(([_, course]: [string, any]) => {
        if (!coursesData[day][sessionId]) {
          coursesData[day][sessionId] = [];
        }
        coursesData[day][sessionId].push(course);
      });
    });
    // console.log("Course Data: ", coursesData);
    setCourses(coursesData);
    setLoadingEDT(false);
  };

  useEffect(() => {
    fetchWorkingDays();
    fetchAllSessions();
    fetchEDTBreaksByYear();
  }, [selectedYear]);

  useEffect(() => {
    setCourses({});
    formattedCourses();
  }, [selectedTimetableData, sessions]);

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="student-edt-page-header">
            <div className="student-edt-title-block">
              <h3 className="page-title mb-1">Emploi du Temps</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.studentDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    EDT
                  </li>
                </ol>
              </nav>
            </div>
            <div className="student-edt-toolbar">
              <EDTTooltipOption
                selectedClass={selectedYear?.Student?.Class}
                onChange={(t) => setSelectedTimetable(t)}
              />
            </div>
          </div>
          {/* /Page Header */}

          <div className="card student-edt-card">
            {/* En-tête sticky : plage de dates */}
            <div className="student-edt-card-header">
              <h5 className="mb-0 student-edt-date-range">
                {selectedTimetable
                  ? `Du ${new Date(
                      selectedTimetableData?.valid_from,
                    ).toLocaleDateString("fr-FR")} au ${new Date(
                      selectedTimetableData?.valid_until,
                    ).toLocaleDateString("fr-FR")}`
                  : "Sélectionner un emplois du temps"}
              </h5>
            </div>

            {/* Grille */}
            <div className="card-body pb-0 student-edt-body">
              {loadingEDT ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Chargement...</span>
                  </div>
                </div>
              ) : courses && Object.keys(courses).length > 0 ? (
                <StudentEDTGrid
                  days={days}
                  sessions={sessions}
                  courses={courses}
                />
              ) : (
                <div className="student-edt-empty">
                  <i className="ti ti-calendar-off student-edt-empty-icon" />
                  <p>Aucun cours prévus.</p>
                </div>
              )}
            </div>

            {/* Pauses */}
            {allBreaks.length > 0 && (
              <div className="card-footer border-0 pt-2 pb-3">
                <p className="text-muted small mb-2 fw-semibold">Pauses :</p>
                <div className="row g-2">
                  {allBreaks.map((brk: any) => (
                    <div
                      key={brk.id}
                      className="col-12 col-sm-6 col-lg-4 col-xxl-3 d-flex"
                    >
                      <div className="card flex-fill mb-0">
                        <div className="card-body py-2 px-3">
                          <span className="bg-info badge badge-sm mb-1">
                            {brk?.label}
                          </span>
                          <p className="text-dark mb-0 small">
                            <i className="ti ti-clock me-1" />
                            {brk?.start} à {brk?.end}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        /* ── Page header ── */
        .student-edt-page-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 12px;
          margin-bottom: 16px;
        }

        .student-edt-title-block {
          flex: 1 1 auto;
          min-width: 0;
        }

        .student-edt-toolbar {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 4px;
        }

        /* ── Card ── */
        .student-edt-card {
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .student-edt-card-header {
          position: sticky;
          top: 0;
          left: 0;
          // z-index: 200;
          background: #fff;
          padding: 12px 20px;
          border-bottom: 1px solid #e8eaf0;
          display: flex;
          align-items: center;
        }

        .student-edt-date-range {
          font-size: 15px;
          font-weight: 600;
          color: #2c3e8c;
        }

        .student-edt-body {
          padding-top: 8px;
          overflow: hidden;
        }

        /* ── État vide ── */
        .student-edt-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 48px 24px;
          color: #9098b1;
          gap: 12px;
        }

        .student-edt-empty-icon {
          font-size: 48px;
          opacity: 0.4;
        }

        /* ── Responsive header ── */
        @media (max-width: 575.98px) {
          .student-edt-page-header {
            flex-direction: column;
            align-items: flex-start;
          }

          .student-edt-toolbar {
            width: 100%;
          }

          .student-edt-card-header {
            padding: 10px 14px;
          }

          .student-edt-date-range {
            font-size: 13px;
          }
        }
      `}</style>
    </>
  );
};

export default StudentEDT;
